import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ExecutionDetails } from '../models/execution-details.models';

export interface RegionSelectionRegion {
  regionId: string;
  label: string;
  filename: string;
  lines: string;
  executionRange?: [number, number];
  timeVariationRange?: [number, number];
}

export interface RegionSelectionSummary {
  arguments: string[];
  executionDetails: ExecutionDetails;
  regions: RegionSelectionRegion[];
}

export interface UploadedFileMetadata {
  fileName: string;
  jsonViewUrl: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AnalysisStateService {
  private fileUploaded = new BehaviorSubject<boolean>(false);
  public fileUploaded$ = this.fileUploaded.asObservable();

  private reset = new Subject<void>();
  public reset$ = this.reset.asObservable();

  private regionSelection = new BehaviorSubject<RegionSelectionSummary | null>(null);
  public regionSelection$ = this.regionSelection.asObservable();

  private uploadedFileMetadata = new BehaviorSubject<UploadedFileMetadata | null>(null);
  public uploadedFileMetadata$ = this.uploadedFileMetadata.asObservable();

  constructor() { }

  notifyFileUploaded(isUploaded: boolean): void {
    this.fileUploaded.next(isUploaded);
  }

  triggerReset(): void {
    this.reset.next();
    this.notifyFileUploaded(false);
    this.regionSelection.next(null);
    this.clearUploadedFile();
  }

  updateRegionSelection(selection: RegionSelectionSummary | null): void {
    this.regionSelection.next(selection);
  }

  setUploadedFile(fileName: string, jsonData: unknown): void {
    this.revokeUploadedFileUrl();
    const jsonViewUrl = this.buildJsonViewUrl(fileName, jsonData);
    this.uploadedFileMetadata.next({ fileName, jsonViewUrl });
  }

  clearUploadedFile(): void {
    this.revokeUploadedFileUrl();
    this.uploadedFileMetadata.next(null);
  }

  private revokeUploadedFileUrl(): void {
    const currentUrl = this.uploadedFileMetadata.value?.jsonViewUrl;
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
    }
  }

  private buildJsonViewUrl(fileName: string, jsonData: unknown): string {
    const formattedJson = JSON.stringify(jsonData, null, 2);
    const jsonFile = new File([formattedJson], fileName, { type: 'application/json' });
    return URL.createObjectURL(jsonFile);
  }
}
